/**
 * AdminImmeubleManualRevenueModal — Conciliation manuelle (Interac, comptant, chèque).
 *
 * Extrait de AdminImmeubleModals.jsx (Batch #14.221).
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Loader2, Banknote, Calendar } from "lucide-react";
import { useCascadingSelectors, MasterPropertyUnitSelectors, ModalShell, PAYMENT_METHODS, MONTHS_FR } from "./_shared";
import { API, authH } from "@/api";


export function AdminImmeubleManualRevenueModal({ open, onClose, initialMasterId = "", lockedMaster = false, onCreated }) {
  const ctx = useCascadingSelectors({ initialMasterId, withTenants: true });
  const today = new Date();
  const [form, setForm] = useState({
    amount: "",
    payment_method: "interac",
    paid_at: today.toISOString().slice(0, 10),
    rent_year: today.getFullYear(),
    rent_month: today.getMonth() + 1,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setForm({
      amount: "",
      payment_method: "interac",
      paid_at: new Date().toISOString().slice(0, 10),
      rent_year: new Date().getFullYear(),
      rent_month: new Date().getMonth() + 1,
      notes: "",
    });
  };

  const submit = async () => {
    if (!ctx.masterId || !form.amount || !form.payment_method || !form.paid_at) {
      toast.error("Champs requis : client, montant, méthode, date");
      return;
    }
    setSubmitting(true);
    try {
      const tenant = ctx.tenants.find((t) => t.id === ctx.tenantId);
      const unit = ctx.units.find((u) => u.id === ctx.unitId);
      const r = await axios.post(`${API}/admin/pms/manual-revenues`, {
        master_id: ctx.masterId,
        property_id: ctx.propertyId || null,
        building_id: ctx.propertyId || null,
        unit_id: ctx.unitId || null,
        unit_label: unit?.unit_label || "",
        tenant_id: ctx.tenantId || null,
        tenant_name: tenant?.display_name || "",
        amount: parseFloat(form.amount),
        payment_method: form.payment_method,
        paid_at: form.paid_at,
        rent_year: form.rent_year,
        rent_month: form.rent_month,
        rent_month_label: `${MONTHS_FR[form.rent_month - 1]} ${form.rent_year}`,
        category: "loyer",
        notes: form.notes || null,
      }, { headers: authH() });
      const receiptInfo = r.data?.receipt;
      if (receiptInfo) {
        toast.success(`Conciliation enregistrée — quittance générée et envoyée`);
      } else {
        toast.success(`Conciliation enregistrée — ${form.amount} $ (${form.payment_method})`);
      }
      onCreated?.(r.data);
      reset();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Conciliation manuelle"
      subtitle="Loyer payé hors Stripe (Interac, comptant, chèque, virement)"
      icon={Banknote}
      iconBg="bg-emerald-500"
      testId="admin-manual-revenue-modal"
    >
      <div className="space-y-4">
        <MasterPropertyUnitSelectors ctx={ctx} includeUnitOptional={false} includeTenant={true} lockedMaster={lockedMaster} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-slate-100">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Montant payé *</label>
            <input
              type="number" step="0.01" min="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="850.00"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-revenue-amount"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Méthode de paiement *</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="admin-revenue-method"
            >
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Date du paiement *
            </label>
            <input
              type="date"
              value={form.paid_at}
              onChange={(e) => setForm({ ...form, paid_at: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
              data-testid="admin-revenue-paid-at"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Mois du loyer payé *</label>
            <div className="flex gap-2">
              <select
                value={form.rent_month}
                onChange={(e) => setForm({ ...form, rent_month: parseInt(e.target.value) })}
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                data-testid="admin-revenue-rent-month"
              >
                {MONTHS_FR.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select
                value={form.rent_year}
                onChange={(e) => setForm({ ...form, rent_year: parseInt(e.target.value) })}
                className="w-24 px-2 py-2 rounded-lg border border-slate-300 text-sm bg-white"
                data-testid="admin-revenue-rent-year"
              >
                {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Notes</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="ex : reçu par Interac #12345"
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="admin-revenue-notes"
          />
        </div>

        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
          ℹ️ Le revenu sera approuvé immédiatement et, si un locataire est sélectionné, une quittance officielle sera générée et envoyée par courriel + Google Drive.
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="admin-revenue-cancel">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="admin-revenue-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
            Enregistrer la conciliation
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
